import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, MoreVertical, X, Users } from 'lucide-react';
import mapi from './api';
import { isPortalArea } from './areas';
import { useMobile } from './MobileContext';
import { MobileShell, MobileHeader, C, MobileSheet} from './MobileShell';
import { formatDate } from '@/lib/utils';
import {
  conversationContext, conversationListTitle, indexContactsByUser, isGroupConversation, personName,
} from './conversationTitle';
import { matchesFreeText } from '@/components/search/SearchBuilder';

function storedArea() {
  try {
    return localStorage.getItem('app_area') || '';
  } catch (_e) {
    return '';
  }
}

function portalRecipients(contacts) {
  const seen = new Set();
  const out = [];
  (contacts || []).forEach((contact) => {
    if (!contact.user_id || contact.is_me) return;
    if (contact.group !== 'VERWALTUNG' && contact.group !== 'UNTERRICHT') return;
    if (seen.has(contact.user_id)) return;
    seen.add(contact.user_id);
    out.push({ id: contact.user_id, full_name: contact.name, username: contact.name });
  });
  return out;
}

export default function MobileMessages() {
  const nav = useNavigate();
  const mobile = useMobile();
  const portal = isPortalArea(mobile?.area || storedArea());
  const [tab, setTab] = useState('alle');
  const [convs, setConvs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState([]);
  const [menu, setMenu] = useState(null);
  const [contactsByUser, setContactsByUser] = useState({});

  const load = useCallback(async () => {
    try {
      const [r, people] = await Promise.all([
        mapi.get('/messages/conversations', { params: { include_archived: true } }),
        mapi.get('/app/contacts').catch(() => ({ data: [] })),
      ]);
      setConvs(r.data || []);
      setContacts(people.data || []);
      setContactsByUser(indexContactsByUser(people.data || []));
    } catch (e) {
      toast.error('Nachrichten konnten nicht geladen werden');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab !== 'new' || portal) return;
    mapi.get('/messages/directory', { params: { q: q || undefined } })
      .then((r) => setDirectory(r.data || [])).catch(() => {});
    mapi.get('/messages/course-directory', { params: { q: q || undefined } })
      .then((r) => setCourses(r.data || [])).catch(() => {});
  }, [tab, q, portal]);

  const togglePick = (user) => {
    setPicked((prev) => {
      if (portal) return prev.some((p) => p.id === user.id) ? [] : [user];
      return prev.some((p) => p.id === user.id)
        ? prev.filter((p) => p.id !== user.id)
        : [...prev, user];
    });
  };

  const startCourse = async (course) => {
    try {
      const r = await mapi.post('/messages/conversations/from-course', { course_id: course.id });
      nav(`/m/chat/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gruppe konnte nicht angeschrieben werden');
    }
  };

  const startSelected = async () => {
    if (!picked.length) return;
    const kind = portal || picked.length === 1 ? 'direct' : 'group';
    const title = kind === 'group' ? picked.map(personName).filter(Boolean).join(', ') : undefined;
    try {
      const r = await mapi.post('/messages/conversations', {
        kind,
        participant_ids: picked.map((p) => p.id),
        ...(title ? { title } : {}),
      });
      nav(`/m/chat/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unterhaltung konnte nicht gestartet werden');
    }
  };

  const markFavorite = async (conv) => {
    const favorite = !conv.favorite;
    try {
      await mapi.post(`/messages/conversations/${conv.id}/favorite`, { favorite });
      setConvs((prev) => prev.map((row) => (row.id === conv.id ? { ...row, favorite } : row)));
      setMenu(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Favorit fehlgeschlagen');
    }
  };

  const archiveConv = async (conv) => {
    try {
      if (conv.archived) {
        await mapi.post(`/messages/conversations/${conv.id}/unarchive`);
      } else {
        await mapi.post(`/messages/conversations/${conv.id}/archive`);
      }
      setConvs((prev) => prev.map((row) => (
        row.id === conv.id ? { ...row, archived: !conv.archived } : row
      )));
      setMenu(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Archivieren fehlgeschlagen');
    }
  };

  const markPersonalUnread = async (conv, unread) => {
    try {
      await mapi.post(`/messages/conversations/${conv.id}/personal-unread`, { unread });
      setConvs((prev) => prev.map((row) => (
        row.id === conv.id
          ? {
            ...row,
            personal_unread: unread,
            unread_count: unread ? Math.max(row.unread_count || 0, 1) : (row.personal_unread && (row.unread_count || 0) <= 1 ? 0 : row.unread_count),
          }
          : row
      )));
      setMenu(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Markierung fehlgeschlagen');
    }
  };

  const isUnread = (c) => (c.unread_count || 0) > 0 || c.personal_unread;
  const matchesTab = (c) => {
    if (tab === 'archiv') return !!c.archived;
    if (c.archived) return false;
    if (tab === 'ungelesen') return isUnread(c);
    if (tab === 'favoriten') return !!c.favorite;
    return true;
  };
  const recipients = portal ? portalRecipients(contacts) : directory;
  const courseRows = portal ? [] : courses;
  const unreadTotal = convs.filter((c) => !c.archived && isUnread(c)).length;
  const filtered = convs.filter(matchesTab)
    .filter((c) => matchesFreeText({ ...c, title: conversationListTitle(c) }, q));

  const fmt = (d) => (d ? formatDate(d) : '');
  const filters = [
    { k: 'alle', label: 'Alle' },
    { k: 'ungelesen', label: `Ungelesen (${unreadTotal})` },
    { k: 'favoriten', label: 'Favoriten' },
    { k: 'archiv', label: 'Archiv' },
  ];
  const emptyText = {
    alle: 'Keine Nachrichten',
    ungelesen: 'Keine ungelesenen Nachrichten',
    favoriten: 'Keine Favoriten',
    archiv: 'Archiv ist leer',
  };

  return (
    <MobileShell onAction={() => { setTab('new'); setPicked([]); }}>
      <MobileHeader title="Nachrichten" onRefresh={load} backTo="/m" />
      <div className="px-4 py-2">
        <div className="flex items-center gap-2 rounded-lg px-3 py-2"
             style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <Search className="w-4 h-4" style={{ color: C.dim }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…"
                 data-testid="mobile-messages-search"
                 className="flex-1 bg-transparent text-sm outline-none" style={{ color: C.text }} />
        </div>
      </div>

      {tab !== 'new' && (
        <div className="flex gap-2 overflow-x-auto px-4 pb-2">
          {filters.map(({ k, label }) => {
            const on = tab === k;
            return (
              <button key={k} type="button" data-testid={`mobile-messages-filter-${k}`}
                      className="shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold"
                      style={{
                        background: on ? C.red : C.card,
                        color: on ? '#fff' : C.text,
                        border: `1px solid ${on ? C.red : C.border}`,
                      }}
                      onClick={() => setTab(k)}>
                {label}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {tab !== 'new' && (
          filtered.length === 0 ? (
            <p className="text-center text-sm mt-10" style={{ color: C.dim }}>
              {emptyText[tab] || 'Keine Nachrichten'}
            </p>
          ) : (
            <div className="space-y-2">
              {filtered.map((c) => {
                const title = conversationListTitle(c);
                const unread = isUnread(c);
                const count = c.unread_count || 0;
                const group = isGroupConversation(c);
                const letters = title.split(/[\s,]+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || '?';
                const context = conversationContext(c, contactsByUser);
                return (
                  <div key={c.id} data-testid={`mobile-conv-${c.id}`}
                       className="flex items-center gap-2 rounded-2xl px-3 py-3"
                       style={{ background: C.card, border: `1px solid ${C.border}` }}>
                    {unread && (
                      <span data-testid={`mobile-conv-unread-${c.id}`}
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ background: C.red }} />
                    )}
                    <button type="button" onClick={() => nav(`/m/chat/${c.id}`)}
                            className="flex min-w-0 flex-1 items-center gap-3 text-left">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                            style={{ background: C.rose, color: C.red }}>
                        {group ? <Users className="h-5 w-5" /> : letters}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className={`truncate ${unread ? 'text-lg font-bold' : 'text-base font-semibold'}`}>{title}</span>
                          <span className="shrink-0 text-[11px]" style={{ color: C.dim }}>{fmt(c.last_message_at)}</span>
                        </span>
                        {context && (
                          <span data-testid={`mobile-conv-context-${c.id}`}
                                className="mt-0.5 block truncate text-xs" style={{ color: C.dim }}>
                            {context}
                          </span>
                        )}
                        <span className="mt-0.5 flex items-center justify-between gap-2">
                          <span className="truncate text-xs" style={{ color: C.dim }}>{c.last_message_preview || '—'}</span>
                          {count > 0 && (
                            <span data-testid={`mobile-conv-unread-count-${c.id}`}
                                  className="min-w-[1.25rem] shrink-0 rounded-full px-1.5 text-center text-[10px] font-bold text-white"
                                  style={{ background: C.red }}>{count}</span>
                          )}
                        </span>
                      </span>
                    </button>
                    <button type="button" aria-label="Weitere Aktionen"
                            data-testid={`mobile-conv-menu-${c.id}`}
                            className="shrink-0 rounded-full p-1.5"
                            style={{ color: C.dim }}
                            onClick={() => setMenu(c)}>
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )
        )}
        {tab === 'new' && (
          <>
          <button type="button" onClick={() => setTab('alle')}
                  className="mb-2 px-1 text-sm font-semibold" style={{ color: C.danger }}>
            Zur Übersicht
          </button>
          {recipients.length === 0 && courseRows.length === 0 ? (
            <p className="text-center text-sm mt-10" style={{ color: C.dim }}>Keine Empfänger gefunden</p>
          ) : (
            <>
              {courseRows.map((course) => (
                <button key={course.id} type="button" onClick={() => startCourse(course)}
                        data-testid={`mobile-newchat-course-${course.id}`}
                        className="w-full text-left px-4 py-3"
                        style={{ borderBottom: `1px solid ${C.border}` }}>
                  <span className="block text-sm font-semibold">{course.label}</span>
                  <span className="block text-[11px]" style={{ color: C.dim }}>
                    Gruppe · {course.participant_count || 0} Teilnehmer
                  </span>
                </button>
              ))}
              {recipients.map((u) => {
                const on = picked.some((p) => p.id === u.id);
                return (
                  <button key={u.id} onClick={() => togglePick(u)}
                          data-testid={`mobile-newchat-${u.id}`}
                          className="w-full text-left px-4 py-3"
                          style={{
                            borderBottom: `1px solid ${C.border}`,
                            background: on ? '#FCEEEE' : 'transparent',
                          }}>
                    <span className="text-sm font-medium">{personName(u) || u.username}</span>
                  </button>
                );
              })}
              {picked.length > 0 && (
                <div className="px-4 py-3">
                  <button type="button" onClick={startSelected}
                          data-testid={picked.length > 1 ? 'mobile-messages-start-group' : 'mobile-messages-start-direct'}
                          className="w-full rounded-xl py-3 text-sm font-bold text-white"
                          style={{ background: C.danger }}>
                    {picked.length > 1 ? `Gruppe schreiben · ${picked.length}` : 'Nachricht schreiben'}
                  </button>
                </div>
              )}
            </>
          )}
          </>
        )}
      </div>

      {menu && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setMenu(null)} data-testid="mobile-conv-menu-sheet">
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-2" style={{ background: C.card }}
               onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold">{conversationListTitle(menu)}</p>
              <button type="button" onClick={() => setMenu(null)} aria-label="Schließen">
                <X className="w-5 h-5" style={{ color: C.dim }} />
              </button>
            </div>
            <button type="button" data-testid="mobile-conv-mark-unread"
                    className="w-full rounded-xl px-3 py-3 text-left text-sm font-semibold"
                    style={{ background: C.bg, border: `1px solid ${C.border}` }}
                    onClick={() => markPersonalUnread(menu, !menu.personal_unread)}>
              {menu.personal_unread ? 'Erinnerung aufheben' : 'Als ungelesen markieren'}
            </button>
            <button type="button" data-testid="mobile-conv-favorite"
                    className="w-full rounded-xl px-3 py-3 text-left text-sm font-semibold"
                    style={{ background: C.bg, border: `1px solid ${C.border}` }}
                    onClick={() => markFavorite(menu)}>
              {menu.favorite ? 'Favorit entfernen' : 'Favorit'}
            </button>
            <button type="button" data-testid="mobile-conv-archive"
                    className="w-full rounded-xl px-3 py-3 text-left text-sm font-semibold"
                    style={{ background: C.bg, border: `1px solid ${C.border}` }}
                    onClick={() => archiveConv(menu)}>
              {menu.archived ? 'Wieder in den Eingang' : 'Archivieren'}
            </button>
          </div>
        </MobileSheet>
      )}
    </MobileShell>
  );
}
